<template>
  <div>
    <p class="sub-title" v-if="subTitle">{{ subTitle }}</p>
    <el-upload
      v-model:file-list="internalFileList"
      :multiple="multiple"
      :action="action"
      :drag="drag"
      :disabled="disabled"
      :before-upload="$emit('beforeUpload')"
      :http-request="(res: any) => $emit('httpRequest', res, type)"
      :accept="accept"
      list-type="picture-card"
      class="custom-upload-item"
      :on-remove="handleInternalRemove"
      :picture-card-size="{ width: '70px', height: '70px' }"
      :hide-upload="isPreview" 
    >
      <template #default>
        <div @click="handleClick">
          <font-awesome-icon icon="upload" class="primary" size="lg" />
          <div class="placeholder-text">{{ label }}</div>
        </div>
      </template>

      <template #file="{ file }">
        <div class="custom-thumb">
          <img :src="file.url" class="preview-img" :alt="file.name"/>
          <div v-if="isPreview" class="overlay" @click.stop="previewImage(file)">
            <el-icon>
              <FullScreen />
            </el-icon>
          </div>
        </div>
      </template>
    </el-upload>
    <el-image-viewer 
      v-if="showPreview" 
      :url-list="[previewImageUrl]" 
      @close="closeImageUrl" 
      :teleported="true" 
    />
  </div>
</template>

<script lang="ts">
import { ElIcon, ElImageViewer, ElUpload } from 'element-plus';
import { Component, Vue, Prop } from 'vue-facing-decorator';
import { FullScreen } from '@element-plus/icons-vue';
import { FileData } from '@/types/enum/file';
import { TemplateUploadImageType } from '@/types/enum/upload';


@Component({
  components: {
    ElUpload,
    ElImageViewer,
    ElIcon,
    FullScreen, 
  },
  emits: [
    'beforeUpload',
    'httpRequest',
    'handleClick',
    'update:fileList',
    'handleRemove',
  ]
})
export default class ImageUploadListV2 extends Vue {
  @Prop({ required: true }) fileList!: FileData[]
  @Prop({ required: true }) type!: TemplateUploadImageType
  @Prop({ default: '' }) label!: string
  @Prop({ default: '' }) tooltipText!: string
  @Prop({ default: 1 }) maxSize!: number
  @Prop({ default: '.jpg,.png,' }) accept!: string
  @Prop({ default: true }) multiple!: boolean
  @Prop({ default: true }) drag!: boolean
  @Prop({ default: false }) disabled!: boolean
  @Prop({ default: true }) isPreview!: boolean
  @Prop({ default: '#' }) action!: string
  @Prop({ default: '' }) subTitle!: string
  @Prop({ type: Function }) readonly handleClick?: (e: Event) => void

  public previewImageUrl = '';
  public showPreview = false;

  get internalFileList(): FileData[] {
    return this.fileList
  }

  set internalFileList(val: FileData[]) {
    this.$emit('update:fileList', val)
  }

  handleInternalRemove(file: FileData) {
    // const updatedList = this.internalFileList.filter((f) => f.uid !== file.uid);
    // this.$emit('update:modelValue', updatedList);
    this.$emit('handleRemove', file, this.type)
  }

  previewImage(file: FileData) {
    if (!this.isPreview) return;
    this.previewImageUrl = file.url;
    this.showPreview = true;
  }

  closeImageUrl() {
    this.showPreview = false;
  }
}
</script>

<style scoped>
.custom-upload-item {
  width: 100%;
}

:deep(.el-upload--picture-card) {
  width: 76px !important;
  height: 76px !important;
}

:deep(.el-upload-list--picture-card .el-upload-list__item) {
  width: 76px !important;
  height: 76px !important;
}

:deep(.el-upload-list--picture-card) {
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 8px !important;
  min-height: 81px !important;
  max-height: 270px !important;
  width: 279px;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.placeholder-text {
  font-size: 12px;
  line-height: 16px;
}

.sub-title {
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.67);
  margin-bottom: 10px;
}

.custom-thumb {
  position: relative;
  width: 76px;
  height: 76px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid var(--el-border-color);
}

.preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.25s ease;
  cursor: pointer;
}

.custom-thumb:hover .overlay {
  opacity: 1;
}

.overlay :deep(.el-icon) {
  font-size: 20px;
  color: white;
}

.file-invalid {
  border: 1px solid red !important;
  border-radius: 4px;
}

::v-deep(.el-upload-list--picture-card .el-upload-list__item) {
  border: none !important;
  box-shadow: none !important;
}

::v-deep(.el-upload.is-disabled .el-upload-dragger) {
  background-color: transparent !important;
}
</style>